import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils } from "@/comn/utils";
import { Page, Group, Layout, Button, FormControl } from "@/comn/components";
import {
    useForm,
    useFetch,
    useModal,
    useStore,
    useToast,
    usePopup,
    useResource,
    useGrid,
    useAuth,
    usePage,
} from "@/comn/hooks";
import {
    BASE,
    URLS,
    APIS,
    SF_TANSAD_DCLR_SRCH,
    SG_TANSAD_DCLR_LST,
} from "@/tz/go/egaz/osci/lpco/cscl/services/OsciTansadDclrService";
import { Grid } from "@/comn/components";
import { lpcoUtils } from "@/tz/go/egaz/osci/lpco/comn";
import dayjs from "dayjs";

export const OSCI0202001Q = (props: any) => {
    useResource({
        defaultSchema: [{ area: "cstmCd" }],
    });
    const auth = useAuth();
    let [query, setQuery] = useSearchParams();
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0001", "auth_osci0002", "auth_osci0003"] /* OGA's TANSAD Information Access */,
    });
    const { t } = useTranslation();
    const navigate = useNavigate();
    const modal = useModal();
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid });
    const toast = useToast();
    const { openPopup } = usePopup();
    const { close, postMessage, getParams } = usePopup();

    const [load, setLoad] = useState(false);

    const setDcltInfo = () => {
        form.dclrTansadDclrSrch.setValues({
            tin: auth.get("tin"),
        });
    };

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0042" }, // 신고서_모드_코드
            { area: "comnCd", comnCd: "OSC0023" }, //통관계획코드
            { area: "comnCd", comnCd: "OSC0033" }, //Selectivity Result
            { area: "comnCd", comnCd: "OSC0018" }, // 신고서_처리_상황_코드
            { area: "comnCd", comnCd: "OSC0019" }, // 통관_외부_처리_상태_코드
            { area: "cstmCd" }, // 세관_코드
            { area: "cntyCd" }, // 국가_코드
        ],
    });

    const formDefaultValues = () => {
        if (query.get("before") === "Y") {
            return {
                page: query.get("page"),
                size: query.get("size"),
            };
        } else {
            return { strtDt: comnUtils.getDate(), endDt: comnUtils.getDate() };
        }
    };

    const coDclaCd = auth.get("coDclaCd");

    const form = {
        dclrTansadDclrSrch: useForm({
            defaultSchema: SF_TANSAD_DCLR_SRCH,
            defaultValues: {
                ...(pgeStore?.form || {
                    reffDtFrom: comnUtils.getDate({ y: 0, m: -1, d: 0 }),
                    reffDtTo: comnUtils.getDate({ y: 0, m: 0, d: 0 }),
                    decrModeCd: ["RACD01", "RASD01", "RASS01"].includes(coDclaCd) ? "IM8" : null,
                    dstnCntyCd:
                        coDclaCd == "RACD01" ? "CD" : coDclaCd == "RASD01" ? "SD" : coDclaCd == "RASS01" ? "SS" : null,
                }),
            },
        }),
    };

    const grid = {
        dclrTansadList: useGrid({
            defaultSchema: SG_TANSAD_DCLR_LST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    const fetch = {
        getDclrTansadList: useFetch({
            api: (page = grid.dclrTansadList.page) => {
                console.log(form.dclrTansadDclrSrch.getValues());
                return APIS.getTansadDclrList(form.dclrTansadDclrSrch.getValues(), page, grid.dclrTansadList.size);
            },
            enabled: comnUtils.isEmpty(form.dclrTansadDclrSrch.errors) && form.dclrTansadDclrSrch.isSubmitted,
            key: [grid.dclrTansadList.page, grid.dclrTansadList.size],
            onSuccess: (data) => {
                setStore(pgeUid, {
                    form: form.dclrTansadDclrSrch.getValues(),
                    page: grid.dclrTansadList.page,
                    size: grid.dclrTansadList.size,
                });
            },
            onError: () => {},
            showToast: true,
        }),
    };

    const handler = {
        getTansadDclrList: (page: number = 0) => {
            form.dclrTansadDclrSrch.handleSubmit(
                (data) => {
                    if (query.get("before") === "Y") {
                        grid.dclrTansadList.setPage(grid.dclrTansadList.page);
                        fetch.getDclrTansadList.fetch(grid.dclrTansadList.page);
                    } else {
                        grid.dclrTansadList.setPage(page);
                        fetch.getDclrTansadList.fetch(page);
                    }
                },
                (data) => {
                    // Data is invalid. (Check the error details for each item)
                    toast.showToast({ type: "warning", content: "msg.osc.00003" });
                },
            )();
        },
        initSet: () => {},
        ketOnReffPaste: (e: any) => {
            const data = e.clipboardData.getData("Text").replace(/[^\w]/g, "");
            if (data.length <= 9) {
                form.dclrTansadDclrSrch.setValue("reffTin", data);
            } else if (data.length > 9 && data.length <= 11) {
                form.dclrTansadDclrSrch.setValue("reffTin", data.substring(0, 9));
                form.dclrTansadDclrSrch.setValue("reffYy", data.substring(9, 11));
            } else if (data.length > 11) {
                form.dclrTansadDclrSrch.setValue("reffTin", data.substring(0, 9));
                form.dclrTansadDclrSrch.setValue("reffYy", data.substring(9, 11));
                form.dclrTansadDclrSrch.setValue("reffSrno", data.substring(11, 18));
            }
        },
        ketOnTansadPaste: (e: any) => {
            const data = e.clipboardData.getData("Text").replace(/[^\w]/g, "");
            if (data.length <= 4) {
                form.dclrTansadDclrSrch.setValue("cstmOfceCd", data);
            } else if (data.length > 4 && data.length <= 6) {
                form.dclrTansadDclrSrch.setValue("cstmOfceCd", data.substring(0, 4));
                form.dclrTansadDclrSrch.setValue("tansadYy", data.substring(4, 6));
            } else if (data.length > 6) {
                form.dclrTansadDclrSrch.setValue("cstmOfceCd", data.substring(0, 4));
                form.dclrTansadDclrSrch.setValue("tansadYy", data.substring(4, 6));
                form.dclrTansadDclrSrch.setValue("tansadSrno", data.substring(6, 13));
            }
        },
    };

    const render = {
        grid_DclrTansadList: {
            head: {},
            cell: {
                reffNo: (props: any) => {
                    const { binding, rowValues, value } = props;
                    return `${rowValues.reffTin}-${rowValues.reffYy}-${rowValues.reffSrno}`;
                },
                tansadNo: (props: any) => {
                    const { binding, rowValues, value } = props;
                    const tansadNo = `${rowValues.cstmOfceCd}-${rowValues.tansadYy}-${rowValues.tansadSrno}`;
                    return (
                        <Link className="text-uf-blue" to={`${URLS.osci0202002q}/${tansadNo}`}>
                            {tansadNo}
                        </Link>
                    );
                },
                dcltTin: (props: any) => {
                    const { binding, rowValues, value } = props;

                    return value;
                },
                csclXtrnPrcssSttsCd: (props: any) => {
                    const { binding, rowValues, value } = props;
                    if (rowValues.csclXtrnPrcssSttsCd == "C") {
                        return (
                            <a
                                href="#!"
                                onClick={(e) => {
                                    e.preventDefault();
                                    modal.openModal({
                                        url: `${URLS.osci0202003q}/${rowValues.reffTin}-${rowValues.reffYy}-${rowValues.reffSrno}`,
                                        params: {
                                            reffTin: rowValues.reffTin,
                                            reffYy: rowValues.reffYy,
                                            reffSrno: rowValues.reffSrno,
                                        },
                                        size: "lg",
                                    });
                                }}
                            >
                                <FormControl
                                    area="comnCd"
                                    comnCd="OSC0019"
                                    viewType="both"
                                    editType="both"
                                    type="select"
                                    edit={false}
                                    value={value}
                                />
                            </a>
                        );
                    } else {
                        return (
                            <FormControl
                                area="comnCd"
                                comnCd="OSC0019"
                                viewType="both"
                                editType="both"
                                type="code"
                                edit={false}
                                value={value}
                            />
                        );
                    }
                },

                dclrPrcssSttsCd: (props: any) => {
                    const { binding, rowValues, value } = props;
                    return (
                        <FormControl
                            area="comnCd"
                            comnCd="OSC0018"
                            viewType="both"
                            editType="both"
                            type="code"
                            edit={false}
                            value={value}
                        />
                    );
                },

                sltvRsltCd: (props: any) => {
                    const { binding, rowValues, value } = props;

                    let color: string = "";
                    let sltvVal = "";

                    if (value == "G") {
                        color = "green";
                        sltvVal = "Green";
                    } else if (value == "Y") {
                        color = "gray";
                        sltvVal = "Yellow";
                    } else if (value == "S") {
                        color = "orange";
                        sltvVal = "Scanning";
                    } else if (value == "R") {
                        color = "red";
                        sltvVal = "Red";
                    }
                    return <span style={{ color: color }}>{sltvVal}</span>;
                },
            },
        },
    };

    const optionYn = [
        { label: "Y", value: "Y" },
        { label: "N", value: "N" },
    ];

    useEffect(() => {
        handler.initSet();
    }, []);

    useEffect(() => {
        if (coDclaCd == "RACD01") {
            form.dclrTansadDclrSrch.setSchema("decrModeCd", { readOnly: true });
            form.dclrTansadDclrSrch.setSchema("dstnCntyCd", { readOnly: true }); // Congo
        } else if (coDclaCd == "RASD01") {
            form.dclrTansadDclrSrch.setSchema("decrModeCd", { readOnly: true });
            form.dclrTansadDclrSrch.setSchema("dstnCntyCd", { readOnly: true }); // Sudan
        } else if (coDclaCd == "RASS01") {
            form.dclrTansadDclrSrch.setSchema("decrModeCd", { readOnly: true });
            form.dclrTansadDclrSrch.setSchema("dstnCntyCd", { readOnly: true }); // South Sudan
        }
    }, [coDclaCd]);
    /*
    useEffect(() => {
        const { unsubscribe } = form.dclrTansadDclrSrch.watch((e) => {
            const { reffDtFrom, reffDtTo } = e;

            const normalizeDate = (date: any) =>
                dayjs.isDayjs(date) ? date : dayjs(typeof date === "string" ? date : dayjs(date).format("YYYY-MM-DD"));

            const start = normalizeDate(reffDtFrom);
            const end = normalizeDate(reffDtTo);
            const oneMonthLater = start.add(1, "month");

            if (reffDtFrom && reffDtTo) {
                const start = normalizeDate(reffDtFrom);
                const end = normalizeDate(reffDtTo);
                const oneMonthLater = start.add(1, "month");

                if (end.isAfter(oneMonthLater)) {
                    const adjustedEnd = oneMonthLater.format("YYYY-MM-DD");

                    modal.openModal({
                        // Search period cannot be more than {0} month.
                        content: t("msg.osc.00138", { 0: 1 }),
                    });
                    // 이미 같은 값이면 setValue 하지 않음 (무한루프 방지)
                    if (end.format("YYYY-MM-DD") !== adjustedEnd) {
                        form.dclrTansadDclrSrch.setValue("reffDtTo", adjustedEnd);
                        form.dclrTansadDclrSrch.setValue("reffDtmRange", [start.format("YYYY-MM-DD"), adjustedEnd]);
                    }
                }
            }
        });

        return () => {
            unsubscribe();
        };
    } ,[form.dclrTansadDclrSrch.watch]);
*/
    return (
        <Page>
            <form>
                <Group>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.dclrTansadDclrSrch.schema.reffDtmRange} />
                            </Group.Row>
                            <Group.Row>
                                <Group.Label {...form.dclrTansadDclrSrch.schema.referenceNo}></Group.Label>
                                <Group.Field size={4}>
                                    <Group.Control
                                        {...form.dclrTansadDclrSrch.schema.reffTin}
                                        onPaste={(e: any) => {
                                            e.preventDefault();
                                            handler.ketOnReffPaste(e);
                                        }}
                                    />
                                    <Group.Any>-</Group.Any>
                                    <Group.Control {...form.dclrTansadDclrSrch.schema.reffYy} />
                                    <Group.Any>-</Group.Any>
                                    <Group.Control {...form.dclrTansadDclrSrch.schema.reffSrno} />
                                </Group.Field>
                                <Group.Label {...form.dclrTansadDclrSrch.schema.tansadNo}></Group.Label>
                                <Group.Field size={4}>
                                    <Group.Control
                                        {...form.dclrTansadDclrSrch.schema.cstmOfceCd}
                                        onPaste={(e: any) => {
                                            e.preventDefault();
                                            handler.ketOnTansadPaste(e);
                                        }}
                                    />
                                    <Group.Any>-</Group.Any>
                                    <Group.Control {...form.dclrTansadDclrSrch.schema.tansadYy} />
                                    <Group.Any>-</Group.Any>
                                    <Group.Control {...form.dclrTansadDclrSrch.schema.tansadSrno} />
                                </Group.Field>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.dclrTansadDclrSrch.schema.decrModeCd}></Group.Control>
                                <Group.Control {...form.dclrTansadDclrSrch.schema.dclrPrcssSttsCd}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.dclrTansadDclrSrch.schema.dstnCntyCd}></Group.Control>
                            </Group.Row>
                            {/*
                            <Group.Row>
                                <Group.Label {...form.dclrTansadDclrSrch.schema.tinLb} required={true}></Group.Label>
                                    <Group.Field size={4}>
                                        <Group.Col combine={true}>
                                            <Group.Control {...form.dclrTansadDclrSrch.schema.tinTpCd}></Group.Control>
                                            <Group.Any></Group.Any>
                                            <Group.Control {...form.dclrTansadDclrSrch.schema.tin}></Group.Control>
                                        </Group.Col>
                                    </Group.Field>
                                </Group.Row>
                            */}
                            <Group.Row>
                                <Group.Control {...form.dclrTansadDclrSrch.schema.entryCstmOfceCd}></Group.Control>
                                <Group.Control {...form.dclrTansadDclrSrch.schema.ucrNo}></Group.Control>
                            </Group.Row>
                        </Group.Section>

                        <Layout direction="row">
                            <Layout.Left>
                                <Button
                                    role="reset"
                                    onClick={() => {
                                        form.dclrTansadDclrSrch.reset();
                                    }}
                                ></Button>
                            </Layout.Left>
                            <Layout.Right>
                                <Button
                                    role="search"
                                    onClick={() => {
                                        handler.getTansadDclrList();
                                    }}
                                ></Button>
                            </Layout.Right>
                        </Layout>
                    </Group.Body>
                </Group>
            </form>

            <Group>
                <Group.Body>
                    <Layout direction="row">
                        <Layout.Left>
                            <Button
                                role="excelDownServer"
                                onClick={() => {
                                    grid.dclrTansadList.exportExcel({
                                        excelName: "Declaration List",
                                        sheetName: "Declaration List ",
                                    });
                                }}
                            />
                        </Layout.Left>
                    </Layout>
                    <Grid
                        {...grid.dclrTansadList.grid}
                        data={fetch.getDclrTansadList.data?.tansadDclrList}
                        render={render.grid_DclrTansadList}
                    />
                </Group.Body>
            </Group>
        </Page>
    );
};
